import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Building2,
  Wrench,
  Truck,
  ArrowLeft,
  ArrowRight,
  Play,
  Pause,
  MessageCircle,
  Bell,
  CheckCircle2,
  Smartphone,
  Bot,
  Zap,
  Clock,
  DollarSign,
  MapPin,
  FileText,
  Send,
  AlertTriangle,
  Users,
  Package,
  Shield,
  ChevronRight,
  ExternalLink,
  SkipForward,
  RotateCcw,
  Volume2,
  VolumeX
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

// AskIsaac Logo
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

// Walkthrough steps data
const WALKTHROUGH_STEPS = [
  {
    id: "welcome",
    title: "Welcome to AskIsaac Telegram",
    subtitle: "Your On-Demand Healthcare Operations Hub",
    description: "AskIsaac connects hospitals, contractors, and suppliers through Telegram for real-time dispatch, tracking, and communication.",
    visual: "welcome",
    duration: 8000,
    highlights: [
      { icon: Building2, text: "Hospitals: Submit requests & track progress", color: "text-blue-400" },
      { icon: Wrench, text: "Contractors: Accept jobs & verify GPS arrival", color: "text-emerald-400" },
      { icon: Truck, text: "Suppliers: Receive RFQs & submit quotes", color: "text-amber-400" }
    ]
  },
  {
    id: "connect",
    title: "Step 1: Connect Your Account",
    subtitle: "Link Telegram to AskIsaac in 30 Seconds",
    description: "From your AskIsaac dashboard, go to Settings → Telegram → Connect. Click the link to open @askisaac_bot and verify your account.",
    visual: "connect",
    duration: 10000,
    steps: [
      "Log into your AskIsaac portal",
      "Go to Settings → Telegram",
      "Click 'Connect Telegram'",
      "Open @askisaac_bot in Telegram",
      "Your account is now linked!"
    ]
  },
  {
    id: "notifications",
    title: "Step 2: Receive Real-Time Alerts",
    subtitle: "Never Miss a Job or Update",
    description: "Once connected, you'll receive instant notifications for new jobs, status updates, contractor arrivals, and quote approvals.",
    visual: "notifications",
    duration: 10000,
    notifications: [
      { type: "job", text: "🔧 New Maintenance Request", detail: "OR Suite 3 - Equipment Down", time: "Just now" },
      { type: "arrival", text: "📍 Contractor Arrived On-Site", detail: "GPS Verified: Building A", time: "2 min ago" },
      { type: "quote", text: "💰 Quote Received", detail: "Backorder #1234 - $450", time: "5 min ago" }
    ]
  },
  {
    id: "commands",
    title: "Step 3: Use Quick Commands",
    subtitle: "Manage Everything from Telegram",
    description: "Type commands to take action instantly. The menu button shows all available commands for your role.",
    visual: "commands",
    duration: 12000,
    commands: [
      { cmd: "/jobs", desc: "View available requests", icon: FileText },
      { cmd: "/status", desc: "Check your active items", icon: Clock },
      { cmd: "/arrived", desc: "Mark GPS arrival (contractors)", icon: MapPin },
      { cmd: "/guide", desc: "Open interaction manual", icon: MessageCircle }
    ]
  },
  {
    id: "workflow-hospital",
    title: "Hospital Workflow",
    subtitle: "Submit → Track → Approve",
    description: "Hospitals can submit maintenance requests and backorders directly through Telegram, then track progress and approve completions.",
    visual: "workflow-hospital",
    duration: 10000,
    workflow: [
      { step: "Submit Request", icon: Send, desc: "/maintenance or /backorder" },
      { step: "Receive Quotes", icon: DollarSign, desc: "Compare contractor bids" },
      { step: "Track Progress", icon: Clock, desc: "Real-time status updates" },
      { step: "Approve & Sign-off", icon: CheckCircle2, desc: "Digital confirmation" }
    ]
  },
  {
    id: "workflow-contractor",
    title: "Contractor Workflow",
    subtitle: "Accept → Arrive → Complete",
    description: "Contractors receive job alerts, accept assignments, verify GPS arrival, and complete work — all from their phone.",
    visual: "workflow-contractor",
    duration: 10000,
    workflow: [
      { step: "Receive Alert", icon: Bell, desc: "New job notification" },
      { step: "Submit Quote", icon: DollarSign, desc: "Bid on the job" },
      { step: "Arrive On-Site", icon: MapPin, desc: "/arrived triggers GPS" },
      { step: "Complete Work", icon: CheckCircle2, desc: "/complete with notes" }
    ]
  },
  {
    id: "workflow-supplier",
    title: "Supplier Workflow",
    subtitle: "Quote → Win → Fulfill",
    description: "Suppliers receive RFQ alerts for backorders, submit competitive quotes, and fulfill orders when selected.",
    visual: "workflow-supplier",
    duration: 10000,
    workflow: [
      { step: "RFQ Alert", icon: Bell, desc: "New quote request" },
      { step: "View Details", icon: FileText, desc: "/quotes to see items" },
      { step: "Submit Quote", icon: Send, desc: "Price + delivery time" },
      { step: "Fulfill Order", icon: Truck, desc: "Ship when approved" }
    ]
  },
  {
    id: "tips",
    title: "Pro Tips for Success",
    subtitle: "Maximize Your Experience",
    description: "Follow these tips to get the most out of AskIsaac Telegram integration.",
    visual: "tips",
    duration: 12000,
    tips: [
      { icon: Zap, text: "Respond quickly — first responder often wins the bid", color: "text-amber-400" },
      { icon: MapPin, text: "Always use /arrived when on-site for GPS verification", color: "text-emerald-400" },
      { icon: AlertTriangle, text: "STAT tier jobs pay premium rates but require 2-hour response", color: "text-red-400" },
      { icon: MessageCircle, text: "Reply to notifications to add comments to tickets", color: "text-cyan-400" }
    ]
  },
  {
    id: "complete",
    title: "You're Ready!",
    subtitle: "Start Using AskIsaac Today",
    description: "Open Telegram and search for @askisaac_bot to get started. Your dashboard awaits!",
    visual: "complete",
    duration: 10000,
    cta: true
  }
];

// Visual components for each step
const StepVisual = ({ step, isActive }) => {
  switch (step.visual) {
    case "welcome":
      return (
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-2xl shadow-cyan-500/30 animate-pulse">
              <Bot className="w-12 h-12 text-white" />
            </div>
            <div className="space-y-4 mt-8">
              {step.highlights.map((h, i) => (
                <div key={i} className={`flex items-center gap-3 justify-center ${isActive ? 'animate-fade-in' : ''}`} style={{ animationDelay: `${i * 200}ms` }}>
                  <h.icon className={`w-6 h-6 ${h.color}`} />
                  <span className="text-slate-300">{h.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    
    case "connect":
      return (
        <div className="relative h-full flex items-center justify-center">
          <div className="bg-slate-800/80 rounded-2xl p-6 max-w-sm border border-slate-700">
            <div className="space-y-3">
              {step.steps.map((s, i) => (
                <div key={i} className={`flex items-center gap-3 ${isActive ? 'animate-fade-in' : ''}`} style={{ animationDelay: `${i * 400}ms` }}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${i <= 4 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>
                    {i < 4 ? <CheckCircle2 className="w-4 h-4" /> : <span className="text-sm font-bold">{i + 1}</span>}
                  </div>
                  <span className={i < 4 ? 'text-slate-300' : 'text-slate-500'}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    
    case "notifications":
      return (
        <div className="relative h-full flex items-center justify-center">
          <div className="space-y-3 w-full max-w-sm">
            {step.notifications.map((n, i) => (
              <div 
                key={i} 
                className={`bg-slate-800/80 rounded-xl p-4 border border-slate-700 transform transition-all ${isActive ? 'animate-slide-in' : ''}`}
                style={{ animationDelay: `${i * 500}ms` }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-white">{n.text}</p>
                    <p className="text-sm text-slate-400">{n.detail}</p>
                  </div>
                  <span className="text-xs text-slate-500">{n.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    
    case "commands":
      return (
        <div className="relative h-full flex items-center justify-center">
          <div className="bg-slate-800/80 rounded-2xl p-6 max-w-sm border border-slate-700">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-700">
              <Bot className="w-5 h-5 text-cyan-400" />
              <span className="font-semibold">@askisaac_bot</span>
            </div>
            <div className="space-y-3">
              {step.commands.map((c, i) => (
                <div key={i} className={`flex items-center gap-3 ${isActive ? 'animate-fade-in' : ''}`} style={{ animationDelay: `${i * 300}ms` }}>
                  <c.icon className="w-5 h-5 text-cyan-400" />
                  <div>
                    <code className="text-cyan-400 font-mono text-sm">{c.cmd}</code>
                    <p className="text-xs text-slate-500">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    
    case "workflow-hospital":
    case "workflow-contractor":
    case "workflow-supplier":
      const colors = {
        "workflow-hospital": "from-blue-500 to-cyan-500",
        "workflow-contractor": "from-emerald-500 to-teal-500",
        "workflow-supplier": "from-amber-500 to-orange-500"
      };
      return (
        <div className="relative h-full flex items-center justify-center">
          <div className="flex items-center gap-4">
            {step.workflow.map((w, i) => (
              <div key={i} className={`text-center ${isActive ? 'animate-scale-in' : ''}`} style={{ animationDelay: `${i * 400}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${colors[step.visual]} flex items-center justify-center mb-2`}>
                  <w.icon className="w-8 h-8 text-white" />
                </div>
                <p className="text-sm font-semibold text-white">{w.step}</p>
                <p className="text-xs text-slate-500">{w.desc}</p>
                {i < step.workflow.length - 1 && (
                  <ChevronRight className="w-5 h-5 text-slate-600 absolute right-0 top-1/2 -translate-y-1/2 hidden md:block" style={{ left: `${(i + 1) * 25}%` }} />
                )}
              </div>
            ))}
          </div>
        </div>
      );
    
    case "tips":
      return (
        <div className="relative h-full flex items-center justify-center">
          <div className="grid grid-cols-2 gap-4 max-w-md">
            {step.tips.map((t, i) => (
              <div 
                key={i} 
                className={`bg-slate-800/80 rounded-xl p-4 border border-slate-700 ${isActive ? 'animate-fade-in' : ''}`}
                style={{ animationDelay: `${i * 300}ms` }}
              >
                <t.icon className={`w-6 h-6 ${t.color} mb-2`} />
                <p className="text-sm text-slate-300">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      );
    
    case "complete":
      return (
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-2xl shadow-emerald-500/30">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <a 
                href="https://t.me/askisaac_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600 rounded-xl font-semibold transition-all"
              >
                <Bot className="w-5 h-5" />
                Open @askisaac_bot
                <ExternalLink className="w-4 h-4" />
              </a>
              <Link to="/user-guides">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <FileText className="w-4 h-4 mr-2" /> View Full Guide
                </Button>
              </Link>
            </div>
          </div>
        </div>
      );
    
    default:
      return null;
  }
};

export default function TelegramWalkthroughPage() {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const step = WALKTHROUGH_STEPS[currentStep];
  const totalSteps = WALKTHROUGH_STEPS.length;

  // Auto-advance logic
  useEffect(() => {
    if (isPlaying) {
      const duration = step.duration;
      const interval = 50; // Update progress every 50ms
      let elapsed = 0;

      progressRef.current = setInterval(() => {
        elapsed += interval;
        setProgress((elapsed / duration) * 100);
      }, interval);

      timerRef.current = setTimeout(() => {
        if (currentStep < totalSteps - 1) {
          setCurrentStep(currentStep + 1);
          setProgress(0);
        } else {
          setIsPlaying(false);
        }
      }, duration);
    }

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(progressRef.current);
    };
  }, [currentStep, isPlaying, step.duration, totalSteps]);

  const goToStep = (index) => {
    setCurrentStep(index);
    setProgress(0);
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      goToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const restart = () => {
    goToStep(0);
    setIsPlaying(true);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-10 h-10 rounded-xl" />
              <div>
                <h1 className="font-bold text-lg tracking-tight">AskIsaac</h1>
                <p className="text-xs text-slate-400">Telegram Walkthrough</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/user-guides">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                  <FileText className="w-4 h-4 mr-2" /> Full Guide
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-slate-800/50 border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-4 py-2">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400 w-16">
              {currentStep + 1} / {totalSteps}
            </span>
            <div className="flex-1 flex gap-1">
              {WALKTHROUGH_STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToStep(i)}
                  className={`h-1.5 rounded-full flex-1 transition-all ${
                    i < currentStep ? 'bg-cyan-500' :
                    i === currentStep ? 'bg-cyan-400' : 'bg-slate-700'
                  }`}
                  data-testid={`step-indicator-${i}`}
                >
                  {i === currentStep && (
                    <div 
                      className="h-full bg-cyan-300 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                </button>
              ))}
            </div>
            <span className="text-sm text-slate-500">
              {Math.ceil((step.duration - (progress / 100 * step.duration)) / 1000)}s
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        <div className="flex-1 max-w-6xl mx-auto px-4 py-8 w-full">
          <div className="grid lg:grid-cols-2 gap-8 h-full">
            {/* Text Content */}
            <div className="flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-full text-slate-400 text-sm mb-4 w-fit">
                Step {currentStep + 1}
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-3">{step.title}</h2>
              <p className="text-xl text-cyan-400 mb-4">{step.subtitle}</p>
              <p className="text-slate-400 text-lg">{step.description}</p>
            </div>

            {/* Visual Content */}
            <div className="bg-slate-800/30 rounded-2xl border border-slate-700/50 p-6 min-h-[300px]">
              <StepVisual step={step} isActive={isPlaying} />
            </div>
          </div>
        </div>
      </main>

      {/* Controls */}
      <footer className="sticky bottom-0 bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/50 py-4">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={restart}
                className="text-slate-400 hover:text-white"
                data-testid="restart-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Restart
              </Button>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={prevStep}
                disabled={currentStep === 0}
                className="border-slate-700 text-slate-400 hover:text-white disabled:opacity-50"
                data-testid="prev-btn"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              
              <Button
                variant="default"
                size="icon"
                onClick={togglePlay}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="play-pause-btn"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={nextStep}
                disabled={currentStep === totalSteps - 1}
                className="border-slate-700 text-slate-400 hover:text-white disabled:opacity-50"
                data-testid="next-btn"
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => goToStep(totalSteps - 1)}
                className="text-slate-400 hover:text-white"
                data-testid="skip-btn"
              >
                Skip <SkipForward className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out forwards;
        }
        .animate-scale-in {
          animation: scale-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
